//-------------------------------------------------------------------------------------------------- Imports
import SubDefaultLayout from "../../components/SubDefaultLayout";
import ListAccordion, { ListAccordionItem } from "../../components/ListAccordion";
import { useDispatch, useSelector } from "react-redux";
import { getGeneralData } from "../../stores/slices/generalDataSlice";
import { useCallback, useEffect, useState } from "react";
import { AppDispatch } from "../../stores/store";
import { getHiredServicesClient, getPaymentDataFlp, validateAllServices } from "../../methods/Methods";
import { Datos, GenericResponse, ResponsePackageActivation, ResponsePackageGroups, ResponseSuscriptorDataHiredService } from "../../types/common";
import Loading from "../../components/Loading";
import { clearAlert, setAlert, showAlert } from "../../stores/slices/alertSlice";
import RenderHiredServicesCards from "./components/RenderHiredServicesCards";
import Slider from "../../components/Slider";
import RenderAllServicesCards from "./components/RenderAllServicesCards";
import { Card, Col, Row, Typography } from "antd";
import { getCount } from "../../stores/slices/countSlice";
import { StyleSheet } from "../../StyleSheet";
import _ from "lodash";
import RenderAllActivationsCards from "./components/RenderAllActivationsCards";
import { fillDataServiceSlice, getConfigurableServiceState } from "../../stores/slices/configurableServicesSlice";
const { Text } = Typography;

//-------------------------------------------------------------------------------------------------- View
const MyServices = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { selectedContract } = useSelector(getGeneralData);
    const { count } = useSelector(getCount);
    // Data
    const [serviceList, setServiceList] = useState<ResponseSuscriptorDataHiredService[]>();
    const [services, setServices] = useState<ResponsePackageGroups[]>([]);
    const [activations, setActivations] = useState<ResponsePackageActivation[]>([]);
    //Control
    const [clientId, setClientId] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingServices, setLoadingServices] = useState<boolean>(false);
    const [hasErrorServices, setHasErrorServices] = useState<boolean>(false);
    const [fullName, setFullName] = useState<string>("");
    const [packageName, setPackageName] = useState<string>("");
    const [fullDateFormat, setFullDateFormat] = useState("");
    const confService = useSelector(getConfigurableServiceState);

    const items: ListAccordionItem[] = [
        {
            key: 0,
            title: "Servicios Contratados",
            children: <RenderHiredServicesCards servicesList={serviceList} />
        },
        {
            key: 1,
            title: "Contrata más Servicios",
            children:
                <RenderAllServicesCards
                    services={services}
                    loading={loadingServices}
                    hasError={hasErrorServices}
                />
        },
        {
            key: 2,
            title: "Activa tus Servicios",
            children:
                <RenderAllActivationsCards
                    activations={activations}
                    loading={loadingServices}
                    hasError={hasErrorServices}
                    dataConf={confService}
                />
        },
    ];

    const getHiredServices = useCallback(async (idCliente: string) => {
        setLoading(true);

        let message = "";
        let messageType: "ERROR" | "INFO" | "SUCCESS" = "INFO";
        let hasError = false;

        await getHiredServicesClient(idCliente)
            .then((response: GenericResponse<ResponseSuscriptorDataHiredService>) => {
                if (response.data[0]) {
                    setServiceList(response.data);
                } else {
                    hasError = true;
                    message = "No fue posible obtener la información de los servicios contratados.\nInténte de nuevo más tarde.";
                    messageType = "ERROR";
                }
                setLoading(false);
            }).catch(() => {
                hasError = true;
                message = "No fue posible obtener la información de los servicios contratados.\nInténte de nuevo más tarde.";
                messageType = "ERROR";
                setLoading(false);
            });

        if (hasError) {
            dispatch(setAlert({
                show: true,
                type: messageType,
                title: "¡Estimado usuario!",
                message: message,
                onFinalize: () => {
                    dispatch(showAlert(false));
                    dispatch(clearAlert);
                }
            }));
        }
    }, [setServiceList, dispatch]);

    const validateServices = useCallback(async (clientId: string) => {
        setLoadingServices(true);
        await validateAllServices(clientId)
            .then((response) => {
                if (response.data.data[0].packageGroups && !_.isEmpty(response.data.data[0].packageGroups)) {
                    setServices(response.data.data[0].packageGroups);
                }
                if (response.data.data[0].activationList && !_.isEmpty(response.data.data[0].activationList)) {
                    setActivations(response.data.data[0].activationList);
                }
                setLoadingServices(false);
                setHasErrorServices(false);
            })
            .catch(() => {
                setLoadingServices(false);
                setHasErrorServices(true);
            })
    }, [setServices]);

    const getPaymentDataCache = useCallback(async (clientId: string) => {
        let datos: Datos | undefined = undefined;

        await getPaymentDataFlp(clientId)
            .then((response) => {
                // Se pregunta si la respuesta no está vacía.
                if (response.data && response.data.data && response.data.data[0].data && response.data.data[0].success) {
                    datos = response.data.data[0].data.datos;
                    if (datos) {
                        let fullDateFormatTmp = "";
                        const [diaStr, mesStr] = datos.paymentLimitDate.split("-");

                        const mesMap: Record<string, string> = {
                            Ene: "Enero",
                            Feb: "Febrero",
                            Mar: "Marzo",
                            Abr: "Abril",
                            May: "Mayo",
                            Jun: "Junio",
                            Jul: "Julio",
                            Ago: "Agosto",
                            Sep: "Septiembre",
                            Oct: "Octubre",
                            Nov: "Noviembre",
                            Dic: "Diciembre",
                        };

                        const monthLong = mesMap[mesStr];
                        const day = parseInt(diaStr, 10);

                        fullDateFormatTmp = `${day} de ${monthLong}`;
                        setFullDateFormat(fullDateFormatTmp);
                    }
                }
            })
            .catch(() => { });
    }, []);

    useEffect(() => {
        dispatch(fillDataServiceSlice())
        if (selectedContract) {
            setClientId(selectedContract.sucursal + selectedContract.suscriptor);
            setFullName(selectedContract.nombre + " " + selectedContract.apellidoPaterno + " " + selectedContract.apellidoMaterno);
            setPackageName(selectedContract.paquete);
        }
    }, [selectedContract, dispatch]);

    useEffect(() => {
        if (clientId) {
            getHiredServices(clientId);
            validateServices(clientId);
            getPaymentDataCache(clientId);
        }
    }, [clientId, getHiredServices, validateServices, getPaymentDataCache, count]);

    return ((loading) ? (
        <Loading />
    ) : (
        <SubDefaultLayout title={"Mis Servicios"}>
            <Slider />
            <Row style={{ marginTop: 20 }} justify={"center"}>
                <Col span={24}>
                    <Card
                        bodyStyle={{
                            justifyContent: "center", display: "flex"
                        }}
                        style={{ ...styles.cardRoot }}
                    >
                        <Row style={{ display: "flex", justifyContent: "center" }}>
                            <div
                                style={{
                                    ...styles.divRoot,
                                    backgroundColor: "#0055c2",
                                }}
                            >
                                <Col style={{ marginTop: 15, marginBottom: 10 }}>
                                    <div>
                                        <Text style={styles.textPrincipal}>
                                            {fullName}
                                        </Text>
                                    </div>
                                    <div>
                                        <Text style={styles.textRegular}>
                                            Suscriptor: {clientId}
                                        </Text>
                                    </div>
                                    <div>
                                        <Text style={styles.textRegular}>
                                            {packageName}
                                        </Text>
                                    </div>
                                </Col>
                            </div>
                            {
                                fullDateFormat &&
                                <div style={styles.footerCard}>
                                    <Text style={styles.textDaysLimit}>Tu fecha de corte es el día <b>{fullDateFormat}</b></Text>
                                </div>
                            }
                        </Row>
                    </Card>
                </Col>
                <Col span={24}>
                    <ListAccordion
                        items={items}
                        focus={0}
                    />
                </Col>
            </Row>
        </SubDefaultLayout >
    ));
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
    rowPrincipal: {
        marginTop: 30,
        display: "flex",
    },
    cardRoot: {
        backgroundColor: "white",
        marginBottom: 15,
        boxShadow: "0px 0px 8px #00000029",
        borderRadius: 20,
    },
    divRoot: {
        width: "100%",
        borderRadius: 18,
        display: "flex",
        textAlign: "center",
        justifyContent: "center",
    },
    textPrincipal: {
        color: "white",
        fontSize: 18,
        fontWeight: 600,
    },
    textRegular: {
        color: "white",
        fontSize: 15,
    },
    footerCard: {
        display: "flex",
        width: "90%",
        padding: 5,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "0px 0px 20px 20px",
        backgroundColor: "#d9d9d9",
    },
    textDaysLimit: {
        color: "#000000",
        textAlign: "center",
        fontSize: 13,
    },
    textHasDebt: {
        color: "#ff0000",
        textAlign: "center",
        fontSize: 15,
        marginTop: 10,
        fontWeight: 500,
    },
    inputContainer: {
        display: "flex",
        flexDirection: "column",
        marginTop: "10px",
        marginLeft: "5px",
        marginRight: "5px",
    },
    inputTextTitle: {
        fontSize: 15,
        color: "#0f5fb1",
    },
    inputText: {
        borderRadius: 0,
        marginTop: 3,
        width: "100%",
    },
    withOverdueColor: {
        color: "#e00614",
    },
    withoutOverdueColor: {
        color: "#0055c2",
    },
});

//-------------------------------------------------------------------------------------------------- Exports
export default MyServices;
